import { omit, pickBy } from "lodash";
import { nanoid } from "nanoid";

import * as settingsApi from "@/apiv2/planner/settings";
import { createAlertAction } from "@/redux/alerts/alerts.actions";
import { createSetterActions } from "@/redux/helpers/action";
import { parseApiError } from "@/utils/parseApiError";

import { MODULE, SERVER_KEYS } from "./constants";
import * as selectors from "./personalSettings.selectors";
import { savedFromServer, valueToServer } from "./serverShape";

export const [setSaved, setIsLoaded] = createSetterActions(MODULE, ["saved", "isLoaded"]);

// A failed read leaves the board on its defaults rather than blocking the Planner.
// A setting saved while the read was loading is newer than the read, so it is kept.
export const fetchPersonalSettings = () => async (dispatch, getState) => {
  const before = selectors.getSaved(getState()) ?? {};
  let fromServer = {};
  try {
    const response = await settingsApi.getPersonalSettings()();
    fromServer = savedFromServer(response?.data);
  } catch (_) {
    // Keep the defaults.
  }
  const current = selectors.getSaved(getState()) ?? {};
  const savedWhileLoading = pickBy(current, (value, key) => before[key] !== value);
  dispatch(setSaved({ ...fromServer, ...savedWhileLoading }));
  dispatch(setIsLoaded(true));
};

// The last save sent for each setting. Requests sent side by side can reach the server out of
// order, so each save of a setting waits for the one before it.
const lastSave = {};

export const savePersonalSetting = (key, value) => async (dispatch, getState) => {
  const before = selectors.getSaved(getState()) ?? {};
  dispatch(setSaved({ ...before, [key]: value }));

  const send = (lastSave[key] ?? Promise.resolve()).then(() =>
    settingsApi.putPersonalSetting(SERVER_KEYS[key], valueToServer(key, value))()
  );
  lastSave[key] = send.catch(() => null);

  try {
    const response = await send;
    // A refused save can reach us as a 200 carrying the app's own web page instead of the settings.
    if (!response?.data || typeof response.data !== "object") {
      throw new Error("Couldn't save your Planner settings. Please try again.");
    }
  } catch (error) {
    // Undo only this key, and only if nothing newer replaced it, so a later save stays.
    const current = selectors.getSaved(getState()) ?? {};
    if (current[key] === value) {
      dispatch(setSaved(key in before ? { ...current, [key]: before[key] } : omit(current, key)));
    }
    const parsed = parseApiError(error);
    dispatch(
      createAlertAction(
        nanoid(),
        parsed.message,
        true,
        "danger",
        undefined,
        undefined,
        undefined,
        parsed.warnings
      )
    );
  }
};
